/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

// SECONDARY_350 is dropped from the inferred type of the JS appColors module,
// so read it through a narrow string-record cast — the same pattern as
// PlayerFormSkeleton, PlayerSearchModal and ScrollSpyLayout/Sidebar.
const palette = colors as Record<string, string>

/** Two rows of three on desktop — enough to read as "the list". */
const CARD_COUNT = 6

/**
 * What the start step shows while a `?team_id=` link commits on the reader's
 * behalf and moves them on.
 *
 * Mirrors the zone it replaces — numbered heading, tab + search row, the team
 * card grid — so nothing jumps, and it never shows real controls for the
 * moment the commit takes: a grid the reader could click while the step is
 * already leaving would invite a choice that is about to be ignored.
 * Decorative, so hidden from screen readers.
 */
const RegistrationStartSkeleton = () => (
  <div css={wrapStyle} aria-hidden='true'>
    <div css={headingStyle}>
      <div css={headingRowStyle}>
        <div css={chipStyle} />
        <div css={titleBarStyle} />
      </div>
      <div css={subtitleBarStyle} />
    </div>
    <div css={bodyStyle}>
      <div css={controlsStyle}>
        <div css={tabsBarStyle} />
        <div css={searchBarStyle} />
      </div>
      <div css={gridStyle}>
        {Array.from({ length: CARD_COUNT }).map((_, index) => (
          <div key={index} css={cardStyle} />
        ))}
      </div>
    </div>
  </div>
)

export default RegistrationStartSkeleton

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

/* The house skeleton bar — see PlayerFormSkeleton. */
const bar = css`
  border-radius: 8px;
  background-color: ${palette.SECONDARY_350};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

/* Heading and body 24px apart, as in ZoneSection; the whole block 24px under
   the event header, as the zones it stands in for. */
const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  margin-top: 24px;
`

/* Title row, then the subtitle 8px below it. */
const headingStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const headingRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const chipStyle = css`
  ${bar}
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
`

const titleBarStyle = css`
  ${bar}
  width: 320px;
  max-width: 70%;
  height: 24px;
`

/* Indented past the chip so it lines up with the title, as in ZoneSection. */
const subtitleBarStyle = css`
  ${bar}
  width: 480px;
  max-width: calc(100% - 36px);
  height: 16px;
  margin-left: 36px;
`

/* EntityZone's stacked rows, 16px apart. */
const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const controlsStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
`

const tabsBarStyle = css`
  ${bar}
  flex: none;
  width: 280px;
  height: 40px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const searchBarStyle = css`
  ${bar}
  flex: 1;
  min-width: 320px;
  height: 40px;

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`

/* TeamsTab's grid: one column, two from 768px, three from 1024px. */
const gridStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

/* TeamCard's footprint — its own 16px radius, not the bar's 8px. */
const cardStyle = css`
  ${bar}
  height: 110px;
  border-radius: 16px;
`
